import * as Sentry from "@sentry/react";

export const initSentry = () => {
  const env = process.env.NODE_ENV;
  const options = {
    dsn: "https://00cf9894f0ed45d2b6525ff164dc0890@o43360.ingest.sentry.io/6513011",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        minReplayDuration: 10000,
        blockAllMedia: false,
      }),
      Sentry.replayCanvasIntegration({
        // Fix CM2-1GB: Prevent cross-origin canvas capture errors
        enableManualSnapshot: true,
      }),
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
    ],
    // Use tracesSampler instead of tracesSampleRate to reduce transaction volume
    // Budget: 100K/month, targeting ~80K to leave headroom
    tracesSampler: (samplingContext) => {
      const name = samplingContext.name || "";
      const url = window.location.pathname;

      // DROP: SignalR/WebSocket - extremely high volume, low diagnostic value
      if (name.includes("accountHub") || name.includes("negotiate")) {
        return 0.0001; // 0.01% - just enough to verify it works
      }

      // DROP: fetch/xhr to high-volume API endpoints
      if (
        name.includes("Analytics/key-event") ||
        name.includes("Admin/events")
      ) {
        return 0.001; // 0.1%
      }
      if (name.includes("job-status") || name.includes("data-change")) {
        return 0.001; // 0.1% - polling endpoints
      }

      // LOW: High-traffic pages
      if (url.includes("/design/catalog") || url.includes("/products")) {
        return 0.02; // 2%
      }
      if (url === "/" || url.includes("/signup") || url.includes("/login")) {
        return 0.05; // 5%
      }

      // MEDIUM: Other navigation
      if (
        name.startsWith("/") ||
        name.includes("pageload") ||
        name.includes("navigation")
      ) {
        return 0.05; // 5%
      }

      // Default: 10% for everything else
      return 0.1;
    },
    // Replay budget: 500/month, replay PAYG $0. Error sessions only (2026-09-23): at 100% of all
    // sessions the quota was gone in the first days of the month, leaving no replay for the
    // sessions that actually errored. Profiling removed the same day: the plan has no profile hours
    // and without a `Document-Policy: js-profiling` header the browser never produced profiles.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    release: "cm-app@" + import.meta.env.VITE_VERSION,
    enabled: env !== "development",
    beforeSend(event, hint) {
      const isSignalRError =
        event.message?.includes("Connection disconnected") ||
        event.message?.includes("Failed to start") ||
        event.message?.includes("Failed to complete");

      if (isSignalRError) {
        return null;
      }

      // Filter client-side network errors (CM2-1RP)
      // These are caused by lost WiFi, mobile browser backgrounding, ad blockers,
      // or DNS failures. Not actionable server-side. 153+ users, mostly mobile.
      if (
        event.exception?.values?.[0]?.type === "AxiosError" &&
        event.exception?.values?.[0]?.value?.includes("Network Error")
      ) {
        return null;
      }

      // Filter client-side request timeouts (CM2-1MT, CM2-1N1, CM2-1NT, CM2-239, etc.)
      // Generated by Axios 60s timeout. Server has its own APM/tracing.
      // These are UX indicators, not server errors. Two surface forms:
      //   - rethrown wrapper:   `Request timeout: ...`
      //   - raw axios message:  `AxiosError: timeout of 60000ms exceeded`
      if (event.message?.startsWith("Request timeout:")) {
        return null;
      }
      if (
        event.exception?.values?.[0]?.type === "AxiosError" &&
        event.exception?.values?.[0]?.value?.includes("timeout of ") &&
        event.exception?.values?.[0]?.value?.includes("ms exceeded")
      ) {
        return null;
      }

      // CM2-2RD: the upload size check in file-util.ts rejects before any request is
      // made. The user is told the limit; there is nothing for us to fix.
      if (event.exception?.values?.[0]?.value?.includes("upload limit. Please split it")) {
        return null;
      }

      // Filter browser translation DOM manipulation errors (CM2-12B, CM2-1M2)
      // These are caused by browser extensions (Edge Translator, Google Translate)
      // directly manipulating the DOM, breaking React's virtual DOM reconciliation.
      // React recovers automatically, so these shouldn't be logged as errors.
      // Mobile Safari throws the same class of error with a different message:
      // "The object can not be found here." (CM2-1M2 — 17 users / 71 events lifetime)
      const isTranslationDOMError =
        event.exception?.values?.[0]?.type === "NotFoundError" &&
        (event.exception?.values?.[0]?.value?.includes("removeChild") ||
          event.exception?.values?.[0]?.value?.includes("insertBefore") ||
          event.exception?.values?.[0]?.value?.includes("replaceChild") ||
          event.exception?.values?.[0]?.value?.includes("The object can not be found here"));

      if (isTranslationDOMError) {
        // Drop entirely - browser translation extensions cause these constantly
        // and React recovers automatically. No diagnostic value.
        return null;
      }

      // Filter native fetch network failures (CM2-1PA)
      // Same family as AxiosError Network Error - client-side connectivity issues.
      if (
        event.exception?.values?.[0]?.type === "TypeError" &&
        event.exception?.values?.[0]?.value === "Failed to fetch"
      ) {
        return null;
      }

      // Filter Firefox SecurityError on history.pushState (CM2-1SV)
      // Firefox blocks pushState in sandboxed/cross-origin contexts. Browser policy, not our bug.
      if (
        event.exception?.values?.[0]?.type === "SecurityError" &&
        event.exception?.values?.[0]?.value?.includes("operation is insecure")
      ) {
        return null;
      }

      // Filter old browser regex incompatibility (CM2-1W2)
      // react-markdown uses Unicode regex (\p{P}) unsupported on iOS <16.4/old Safari.
      if (
        event.exception?.values?.[0]?.type === "SyntaxError" &&
        event.exception?.values?.[0]?.value?.includes("invalid group specifier")
      ) {
        return null;
      }

      // Filter iOS in-app-WebView recursion (CM2-2KM / 2KN / 2KP and ~16 prior fingerprints)
      // "RangeError: Maximum call stack size exceeded" with frames that are entirely NOT ours:
      // `/SignUp:224 (Fk)` -> `(Hk)` -> `(Fk)` ... , a mutual recursion inside Google's injected
      // sign-in/autofill bridge. `/SignUp` (capital S) is not one of our routes.
      // Evidence it is environmental, not a component bug: over 90d this fires on 19 DIFFERENT routes
      // (/products, /catalogs, /design/cards, /signup, /showrooms, /account/billing/plan, ...) with no
      // concentration anywhere, and 52 of 53 events are iOS Chrome / the Google iOS app. Same class as
      // the Meta `_AutofillCallbackHandler` pattern - a per-URL fingerprint explosion that archiving
      // issues one at a time can never clear.
      // The in-app-frame guard is the point: if OUR code ever genuinely blows the stack (a bad recursive
      // render, a cyclic reduce) those frames carry /assets/ and the event still reports. Do not
      // simplify this to a bare message match.
      // Guard on /assets/ only, NOT in_app: the injected script's frames report the PAGE url
      // (`/signup`) and Sentry marks those in_app, which let CM2-2NV through. Our bundle is always /assets/.
      if (
        event.exception?.values?.[0]?.type === "RangeError" &&
        event.exception?.values?.[0]?.value?.includes(
          "Maximum call stack size exceeded"
        ) &&
        !event.exception?.values?.[0]?.stacktrace?.frames?.some((f) =>
          f.filename?.includes("/assets/")
        )
      ) {
        return null;
      }

      // Filter browser-extension errors (CM2-2ME): an extension's own webextension-polyfill, injected as
      // blob: scripts, calling runtime.getManifest in our page. Keep the /assets/ guard so a real
      // error in our bundle with the same text would still report.
      if (
        event.exception?.values?.[0]?.value?.includes(
          "runtime.getManifest is not a function"
        ) &&
        !event.exception?.values?.[0]?.stacktrace?.frames?.some((f) =>
          f.filename?.includes("/assets/")
        )
      ) {
        return null;
      }

      // Filter link-scanner / extension bridge messages (CM2-2PT; V1 half is CM1-13YN): a script
      // injected into the page rejects with "Object Not Found Matching Id:N, MethodName:..." and
      // no stack. The text is the injected bridge's own; nothing in our bundle produces it.
      if (
        event.exception?.values?.[0]?.value?.includes(
          "Object Not Found Matching Id:"
        )
      ) {
        return null;
      }

      // Filter unreadable single-token rejections (CM2-2N9 "La", CM2-2M0 "Ca", batch-8 "Ba"/"ga"):
      // an unhandled rejection whose whole message is 1-3 letters and which carries NO stack frames,
      // all from iOS in-app browsers. The watch pattern's decision check was "do any carry frames?";
      // none did, so it is the WebView, not a source-map gap.
      // Widened 2026-09-23 (CM2-2PH "pa", CM2-2PJ "Ka`prod", CM2-2P3 "La"): the same WebView also
      // throws them via `onerror` and with an inline document frame (`/images:415`), so the guard is
      // now "no /assets/ frame" (as in the RangeError filter) rather than "no frames at all".
      // An /assets/ frame -> still reports.
      const tokenEx = event.exception?.values?.[0];
      if (
        tokenEx?.type === "Error" &&
        /^[A-Za-z`]{1,8}$/.test(tokenEx.value ?? "") &&
        ["onunhandledrejection", "onerror"].includes(tokenEx.mechanism?.type ?? "") &&
        // /assets/ only, never in_app: the SDK marks every browser frame in_app (see RangeError above)
        !tokenEx.stacktrace?.frames?.some((f) => f.filename?.includes("/assets/"))
      ) {
        return null;
      }

      // Filter MUI DataGrid internal race conditions (CM2-13R, CM2-1YF)
      // MUI throws MissingRowIdError or null reference errors when mousedown events
      // fire on rows removed from state during upload/delete. Known MUI issue.
      if (
        event.exception?.values?.some(
          (v) =>
            v.value?.includes("MissingRowIdError") ||
            v.value?.includes("No row with id")
        )
      ) {
        return null;
      }
      // CM2-1YF: MUI DataGrid rowNode null — same race condition, different error surface
      if (
        event.exception?.values?.[0]?.type === "TypeError" &&
        event.exception?.values?.[0]?.stacktrace?.frames?.some(
          (f) => f.filename?.includes("x-data-grid")
        ) &&
        !event.exception?.values?.[0]?.stacktrace?.frames?.some(
          (f) =>
            f.filename?.includes("/src/") && !f.filename?.includes("node_modules")
        )
      ) {
        return null;
      }

      // Filter MUI DataGrid "empty height" console warning (CM2-1CA)
      // "MUI X: useResizeContainer - The parent DOM element ... has an empty height" fires
      // when a DataGrid mounts in a height:0 container (hidden tab / collapsed flex parent /
      // mounted before layout). Cosmetic — the grid renders once the container has height.
      // Captured via the console integration (not a thrown error).
      if (
        event.message?.includes("has an empty height") ||
        event.exception?.values?.some((v) =>
          v.value?.includes("has an empty height")
        )
      ) {
        return null;
      }

      // Filter 401 token expiry errors (CM2-1YR, CM2-1YC, CM2-1XT)
      // JWT expires during long sessions. User gets redirected to login automatically.
      if (
        event.exception?.values?.[0]?.type === "AxiosError" &&
        event.exception?.values?.[0]?.value?.includes("status code 401")
      ) {
        return null;
      }

      // Filter 402 Payment Required (CM2-SW)
      // Intended paywall/credit-gate business state (out of Concierge credits, plan limit,
      // gated feature) — not a bug. The axios interceptor reports non-2xx as an error; the
      // app shows the paywall UI. (Paywall hit-rate is a monetization signal, not a Sentry one.)
      if (
        event.exception?.values?.[0]?.type === "AxiosError" &&
        event.exception?.values?.[0]?.value?.includes("status code 402")
      ) {
        return null;
      }


      // Filter handled 404 "resource not found" on GET reads (CM2-SW)
      // A GET that 404s means the resource (catalog/concierge/template/layout by id)
      // no longer exists — deleted, stale/shared link, or wrong account. The app already
      // handles it (not-found UI); the axios interceptor just rethrows it as an AxiosError.
      // Scoped to GET on purpose: a 404 on a write (POST/PUT/DELETE) can be a real routing/
      // logic bug, so those still surface. Confirmed via breadcrumbs: the failing request is
      // e.g. `GET /api/v1/catalogs/get?id=<guid>` -> 404. If the method can't be read from the
      // original exception, the event is kept (fail-open, never over-filters).
      const axios404Original = hint?.originalException as
        | { config?: { method?: string }; response?: { config?: { method?: string } } }
        | undefined;
      const axios404Method = (
        axios404Original?.config?.method ?? axios404Original?.response?.config?.method
      )?.toLowerCase();
      if (
        event.exception?.values?.[0]?.type === "AxiosError" &&
        event.exception?.values?.[0]?.value?.includes("status code 404") &&
        axios404Method === "get"
      ) {
        return null;
      }

      // Filter reCAPTCHA timeouts (CM2-1YG, CM2-1YE, CM2-1Y7)
      // Transient — reCAPTCHA script load failures on slow connections. Not actionable.
      if (
        event.exception?.values?.[0]?.value?.includes("reCAPTCHA Timeout")
      ) {
        return null;
      }

      // Filter Remirror RTE getRootProps double-attach invariant (CM2-23R, CM2-229 — RMR0201)
      // Benign framework race: the editor's getRootProps ref-spread runs twice on a single
      // mount (Safari / StrictMode double-invoke); Remirror throws but the editor recovers and
      // works. 3rd occurrence across triage batches (open Q #25). Match the fixed invariant
      // substring — the "Called N times" suffix is variable.
      const isRemirrorDoubleAttach =
        event.message?.includes("has been attached to the DOM more than once") ||
        event.exception?.values?.some((v) =>
          v.value?.includes("has been attached to the DOM more than once")
        );
      if (isRemirrorDoubleAttach) {
        return null;
      }

      return event;
    },
  };
  Sentry.init(options);
};
